import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import { ArrowLeft, Sparkles, FileText, Wand2 } from "lucide-react";
import { Button } from "@core/button";
import { getEnvironment } from "@packages/utils";

import CreateModal from "@features/tools/components/create-modal";
import LoadingContainer from "@packages/components/Loading";
import ErrorComponent from "@features/tools/components/error-component";
import {
  getAllSuperTools,
  useCreateSuperTool,
  useGetAllSuperTools,
  useGetSuperToolById,
  useGetRealWorldConnection,
  useDeleteSuperTool,
  getGetAllSuperToolsQueryKey,
} from "@packages/api/madlenAPI";
import SuperToolView from "../components/SuperToolView";
import {
  SuperToolCreateRequestDto,
  SuperToolResponseDto,
  SuperToolCreateRequestDtoCourseName,
  SuperToolCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";
import HeroTitle from "@features/tools/components/tool_components/HeroTitle";
import Text from "@core/text";
import { exampleTopics } from "@features/media-academy/components/CreatePodcastCard";
import Card from "@features/tools/components/card";
import { useNavigate } from "react-router-dom";
import Pagination from "@core/pagination";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";

interface SuperToolResponse {
  state: {
    data: SuperToolResponseDto | null;
  };
}

interface LocationState {
  autoSubmit?: boolean;
  input?: SuperToolCreateRequestDto;
}

interface CreateModalWrapperProps {
  initialInput: SuperToolCreateRequestDto;
  onSubmit: (input: SuperToolCreateRequestDto) => Promise<void>;
  error: Error | null;
}

const ShimmerCard = () => {
  return (
    <div className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden ">
      <div className="relative h-48 w-full">
        {/* Delete button placeholder */}
        <div className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />

        {/* Main content area with shimmer effect */}
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-t-lg relative overflow-hidden">
          <div className="absolute inset-0">
            {/* Multiple shimmer lines with slightly different animation delays */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-gray-600/20 to-transparent"
              style={{ animationDelay: "0ms" }}
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 dark:via-gray-600/10 to-transparent"
              style={{ animationDelay: "300ms" }}
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 dark:via-gray-600/15 to-transparent"
              style={{ animationDelay: "600ms" }}
            />
          </div>
        </div>

        {/* Text area with shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="absolute bottom-4 left-4 right-4 space-y-2">
            {/* Tags placeholder */}
            <div className="flex gap-2">
              <div className="h-5 w-16 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse" />
              <div
                className="h-5 w-16 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"
                style={{ animationDelay: "200ms" }}
              />
            </div>

            {/* Title placeholder */}
            <div
              className="h-6 w-3/4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"
              style={{ animationDelay: "400ms" }}
            />

            {/* Description placeholder with staggered animation */}
            <div
              className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded animate-pulse"
              style={{ animationDelay: "600ms" }}
            />
            <div
              className="h-4 w-2/3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"
              style={{ animationDelay: "800ms" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SuperToolCard = ({
  tool,
  onSelect,
  onDelete,
}: {
  tool: SuperToolResponseDto;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
}) => {
  const { data: connectionData, isLoading: isConnectionLoading } =
    useGetRealWorldConnection(Number(tool.realWorldConnectionId), {
      query: {
        enabled: !!tool.realWorldConnectionId,
      },
    });

  if (!connectionData || isConnectionLoading) {
    return <ShimmerCard />;
  }

  return (
    <Card
      key={tool.id}
      item={{
        ...connectionData,
        gradeLevel:
          connectionData?.gradeLevel === -1 ? 0 : connectionData?.gradeLevel,
      }}
      selectItem={() => onSelect(tool.id ?? 0)}
      setShowDeletePopup={() => onDelete(tool.id ?? 0)}
    />
  );
};

const CreateSuperTool: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const queryClient = useQueryClient();
  const [toolId, setToolId] = useState<number | null>(null);
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const [toolMode, setToolMode] = useState<"BASIC" | "ADVANCED">("BASIC");
  const [topic, setTopic] = useState<string>("");
  const [showDeletePopup, setShowDeletePopup] = useState<number | null>(null);
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 9;
  const { addToast } = useToast();

  const { mutateAsync: createTool, isPending: isCreating } =
    useCreateSuperTool();

  const deleteTool = useDeleteSuperTool({
    mutation: {
      onMutate: async ({ id }: { id: number }) => {
        const getSuperToolsQueryKey = getGetAllSuperToolsQueryKey({
          page: currentPage,
          size: pageSize,
        });
        const previousData = queryClient.getQueryData(getSuperToolsQueryKey);

        queryClient.setQueryData(getSuperToolsQueryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter(
              (tool: SuperToolResponseDto) => tool.id !== id,
            ),
          };
        });

        return { previousData };
      },
      onError: (error: any, _variables: any, context: any) => {
        const getSuperToolsQueryKey = getGetAllSuperToolsQueryKey({
          page: currentPage,
          size: pageSize,
        });
        if (context?.previousData) {
          queryClient.setQueryData(getSuperToolsQueryKey, context.previousData);
        }
        console.error("Failed to delete superTool:", error);
      },
      onSettled: () => {
        const getSuperToolsQueryKey = getGetAllSuperToolsQueryKey({
          page: currentPage,
          size: pageSize,
        });
        queryClient.invalidateQueries({ queryKey: getSuperToolsQueryKey });
      },
    },
  });

  const {
    data: allUserSuperTools,
    isLoading: isAllUserSuperToolsLoading,
    error: allUserSuperToolsError,
  } = useGetAllSuperTools(
    {
      page: currentPage,
      size: pageSize,
    },
    {
      query: {
        queryFn: async () => {
          const response = await getAllSuperTools({
            page: currentPage,
            size: pageSize,
          });

          return response;
        },
      },
    },
  );

  const initialInput = useMemo<SuperToolCreateRequestDto>(
    () => ({
      courseName: "HISTORY" as SuperToolCreateRequestDtoCourseName,
      gradeLevel: 8,
      prompt: "",
      environment: getEnvironment(),
      isPublic: true,
      generationType: "PROMPT" as SuperToolCreateRequestDtoGenerationType,
    }),
    [],
  );

  // Updated to support German language
  const randomExampleTopics = useMemo(() => {
    const topics =
      exampleTopics[i18n.language as "tr" | "en" | "de"] || exampleTopics["en"];
    return topics.sort(() => Math.random() - 0.5).slice(0, 8);
  }, [i18n.language]);

  const {
    data: toolData,
    isLoading: isToolDataLoading,
    error: toolDataError,
  } = useGetSuperToolById(toolId, {
    query: {
      enabled: !!toolId && !dataFetched,
      refetchInterval: (data: SuperToolResponse) => {
        if (data?.state?.data?.id) {
          setDataFetched(true);
          return false;
        }
        return 2000;
      },
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

  const isLoading = isCreating || isToolDataLoading;
  const error = toolDataError;

  const handleSubmit = useCallback(
    async (input: SuperToolCreateRequestDto): Promise<void> => {
      try {
        setDataFetched(false);
        const result = await createTool({ data: input });
        setToolId(Number(result.id));
        queryClient.invalidateQueries({
          queryKey: ["allUserSuperTools"],
        });
      } catch (error) {
        console.error("Error creating tool:", error);
      }
    },
    [createTool, queryClient],
  );

  const handleDelete = async (toolId: number) => {
    try {
      await deleteTool.mutateAsync({ id: toolId });
      setShowDeletePopup(null);
      addToast({
        title: t("superTool.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error deleting super tool:", error);
      addToast({
        title: t("superTool.deleteError"),
        description: t("superTool.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage > 0 &&
      (!allUserSuperTools?.totalPages ||
        newPage <= allUserSuperTools?.totalPages)
    ) {
      setCurrentPage(newPage);
      // Scroll to top below the create modal when changing pages
      window.scrollTo({ top: 450, behavior: "smooth" });
    }
  };

  const shouldShowPagination =
    !allUserSuperToolsError &&
    allUserSuperTools?.totalPages &&
    allUserSuperTools?.totalPages > 1 &&
    !isAllUserSuperToolsLoading;

  // Updated to handle German language in the prompt
  const handleBasicSubmit = useCallback(() => {
    const trimmedTopic = topic?.trim();
    if (!trimmedTopic) return;

    // Create language-specific prompt format
    const getPromptFormat = (language: string, topic: string) => {
      switch (language) {
        case "tr":
          return `Ders Adı:  Hedef Kitle:  Prompt: ${topic} `;
        case "de":
          return `Unterrichtsname:  Zielgruppe:  Eingabe: ${topic} `;
        default:
          return `Lesson Name:  Target Audience:  Prompt: ${topic} `;
      }
    };

    const basicInput = {
      ...initialInput,
      courseName: "UNKNOWN",
      gradeLevel: -1,
      prompt: getPromptFormat(i18n.language, trimmedTopic),
      environment: getEnvironment(),
      isPublic: true,
      generationType: "PROMPT",
    };

    handleSubmit(basicInput);
  }, [initialInput, handleSubmit, topic, i18n.language]);

  const handleTopicClick = useCallback((selectedTopic: string) => {
    setTopic(selectedTopic);
  }, []);

  useEffect(() => {
    if (locationState?.autoSubmit && locationState.input) {
      handleSubmit(locationState.input);
    }
  }, [locationState, handleSubmit]);

  useEffect(() => {
    if (toolData?.id && !dataFetched) {
      setDataFetched(true);
    }
  }, [toolData, dataFetched]);

  const resetToolData = useCallback(() => {
    setToolId(null);
    setDataFetched(false);
    navigate("/super-tool");
  }, [navigate]);

  const CreateModalWrapper: React.FC<CreateModalWrapperProps> = ({
    initialInput,
    onSubmit,
    error,
  }) => (
    <CreateModal
      title={t("superTool.title")}
      backPath="/super-tool"
      isSuperTool
      themeColor="blue"
      loadingTitle={t("superTool.result.loading.title")}
      errorTitle={t("superTool.result.error.title")}
      errorDescription={t("superTool.result.error.description")}
      submitButtonText={t("common.generate")}
      placeholder={t("superTool.form.promptPlaceholder")}
      initialInput={initialInput}
      onSubmit={onSubmit}
      error={error}
    />
  );

  if (isLoading && !toolData) {
    return (
      <LoadingContainer
        title={t("superTool.result.loading.title")}
        themeColor="blue"
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("superTool.result.error.title")}
        description={t("superTool.result.error.description")}
      />
    );
  }

  if (toolData) {
    return (
      <div className="relative">
        <div className="container max-w-6xl mx-auto px-4 py-4">
          <Button
            onClick={resetToolData}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gray-400 dark:bg-gray-700"
            rounded="lg"
            className="mb-2"
          >
            <ArrowLeft size={24} />
          </Button>

          <SuperToolView toolData={toolData} />
        </div>
      </div>
    );
  }
  const isTopicValid = topic?.trim()?.length > 0;

  return (
    <div className="min-h-screen p-4">
      <HeroTitle
        title={t("superTool.title")}
        description={t("superTool.description")}
        titleStyle="bg-gradient-to-r from-cyan-600 via-blue-600 to-rose-600 dark:from-cyan-400 dark:via-blue-400 dark:to-rose-400"
        titleBackgroundStyle="bg-gradient-to-r from-cyan-50/30 to-blue-50/30 dark:from-cyan-900/30 dark:to-blue-900/30"
      />

      <div className="mt-8 px-4">
        <div className="space-y-6">
          <Text
            variant="h3"
            className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400"
          >
            {t("superTool.selectInputType")}
          </Text>

          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={() => setToolMode("BASIC")}
              rounded="xl"
              className={`p-4 border-2 transition-all duration-200 ${
                toolMode === "BASIC"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 hover:border-blue-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Sparkles className="w-5 h-5" />
                <span>{t("superTool.basicTools")}</span>
              </div>
            </Button>

            <Button
              onClick={() => setToolMode("ADVANCED")}
              rounded="xl"
              className={`p-4 border-2 transition-all duration-200 ${
                toolMode === "ADVANCED"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-200 hover:border-blue-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>{t("superTool.advancedTool")}</span>
              </div>
            </Button>
          </div>

          {toolMode === "BASIC" ? (
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={t("superTool.topicPlaceholder")}
                  className="w-full p-4 pl-12 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800/80 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <Wand2
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-4 topic-suggestion">
                {randomExampleTopics.map((example) => (
                  <Button
                    key={example}
                    type="button"
                    onClick={() => handleTopicClick(example)}
                    data-testid="topic-suggestion"
                    variant="solid"
                    size="sm"
                    rounded="full"
                    textColor="text-gray-700 dark:text-gray-300"
                    bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80"
                    className="transition-all duration-200 hover:scale-105"
                  >
                    {example}
                  </Button>
                ))}
              </div>

              <Button
                variant="solid"
                size="lg"
                rounded="xl"
                fullWidth
                bgColor={`${
                  isTopicValid
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
                textColor={`${
                  isTopicValid
                    ? "text-white"
                    : "text-gray-500 dark:text-gray-400"
                }`}
                className={`shadow-lg transition-all duration-200 ${
                  isTopicValid
                    ? "hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-blue-500/50 cursor-pointer"
                    : "cursor-not-allowed"
                }`}
                disabled={!isTopicValid}
                onClick={handleBasicSubmit}
              >
                {t("superTool.generateButton")}
              </Button>
            </div>
          ) : (
            <CreateModalWrapper
              initialInput={initialInput}
              onSubmit={handleSubmit}
              error={error}
            />
          )}

          <div className="space-y-4">
            <div className="flex items-center col-span-2">
              <Text variant="h3">{t("superTool.myTools")}</Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allUserSuperTools?.data?.map((tool) => (
                <SuperToolCard
                  key={tool.id}
                  tool={tool}
                  onSelect={(id) => setToolId(id)}
                  onDelete={(id) => setShowDeletePopup(id)}
                />
              ))}
            </div>
            {!!shouldShowPagination && (
              <Pagination
                currentPage={currentPage}
                totalPages={Number(allUserSuperTools?.totalPages || 1)}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeletePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {t("superTool.deleteConfirmation")}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t("superTool.deleteConfirmationMessage")}
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setShowDeletePopup(null)}
                variant="outline"
                size="md"
                rounded="lg"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => handleDelete(showDeletePopup)}
                variant="solid"
                size="md"
                rounded="lg"
                bgColor="bg-red-600 hover:bg-red-700"
                textColor="text-white"
                disabled={deleteTool.isPending}
              >
                {deleteTool.isPending
                  ? t("common.deleting")
                  : t("common.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateSuperTool;
